import axios from "axios";
import { buildApiUrl, getApiBaseUrl } from "../../config/api";
import {
    getRequest,
    getExpensesSuccess,
    getCategoriesSuccess,
    getBudgetsSuccess,
    getDetailSuccess,
    getSummarySuccess,
    stuffDone,
    getFailed,
    getError,
    underExpenseControl,
} from "./expenseSlice";

function expenseErrorMessage(error) {
    if (!error) return "Request failed";
    if (typeof error === "string") return error;
    const data = error.response?.data;
    if (typeof data?.message === "string") return data.message;
    if (typeof data === "string" && data.trim()) return data;
    if (error.message) return error.message;
    return "Request failed";
}

export const getAllCategories = (schoolId) => async (dispatch) => {
    if (!schoolId) {
        dispatch(getFailed("School id is missing. Sign out and sign in again."));
        return;
    }
    dispatch(getRequest());

    try {
        const result = await axios.get(buildApiUrl(`/ExpenseCategories/${schoolId}`));
        const data = result.data;
        if (data?.message && !Array.isArray(data) && !Array.isArray(data?.categories)) {
            dispatch(getFailed(data.message));
            return;
        }
        const list = Array.isArray(data) ? data : Array.isArray(data?.categories) ? data.categories : [];
        dispatch(getCategoriesSuccess(list));
    } catch (error) {
        // Auto-heal for first-time schools where categories are not yet seeded.
        try {
            const seeded = await axios.post(buildApiUrl(`/ExpenseCategories/${schoolId}/seed`));
            const seededData = seeded.data;
            const list = Array.isArray(seededData)
                ? seededData
                : Array.isArray(seededData?.categories)
                  ? seededData.categories
                  : [];
            dispatch(getCategoriesSuccess(list));
        } catch (seedError) {
            dispatch(getError(expenseErrorMessage(seedError) || expenseErrorMessage(error)));
        }
    }
};

export const createCategory = (data) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.post(`${getApiBaseUrl()}/ExpenseCategory`, data, {
            headers: { "Content-Type": "application/json" },
        });
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(expenseErrorMessage(error)));
    }
};

export const updateCategory = (id, data) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.put(`${getApiBaseUrl()}/ExpenseCategory/${id}`, data, {
            headers: { "Content-Type": "application/json" },
        });
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(expenseErrorMessage(error)));
    }
};

export const deleteCategory = (id) => async (dispatch) => {
    dispatch(getRequest());

    try {
        await axios.delete(`${getApiBaseUrl()}/ExpenseCategory/${id}`);
        dispatch(stuffDone());
    } catch (error) {
        dispatch(getError(expenseErrorMessage(error)));
    }
};

export const getAllExpenses = (schoolId, filters = {}) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const params = new URLSearchParams(filters).toString();
        const result = await axios.get(buildApiUrl(`/Expenses/${schoolId}?${params}`));
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getExpensesSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(expenseErrorMessage(error)));
    }
};

export const createExpense = (data) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.post(`${getApiBaseUrl()}/Expense`, data, {
            headers: { "Content-Type": "application/json" },
        });
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(expenseErrorMessage(error)));
    }
};

export const getExpenseById = (id) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.get(`${getApiBaseUrl()}/Expense/detail/${id}`);
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getDetailSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(expenseErrorMessage(error)));
    }
};

export const updateExpense = (id, data) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.put(`${getApiBaseUrl()}/Expense/${id}`, data, {
            headers: { "Content-Type": "application/json" },
        });
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(expenseErrorMessage(error)));
    }
};

export const approveExpense = (id, data) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.put(`${getApiBaseUrl()}/Expense/${id}/approve`, data, {
            headers: { "Content-Type": "application/json" },
        });
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(expenseErrorMessage(error)));
    }
};

export const rejectExpense = (id, data) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.put(`${getApiBaseUrl()}/Expense/${id}/reject`, data, {
            headers: { "Content-Type": "application/json" },
        });
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(expenseErrorMessage(error)));
    }
};

export const deleteExpense = (id) => async (dispatch) => {
    dispatch(getRequest());

    try {
        await axios.delete(`${getApiBaseUrl()}/Expense/${id}`);
        dispatch(stuffDone());
    } catch (error) {
        dispatch(getError(expenseErrorMessage(error)));
    }
};

export const setBudget = (data) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const result = await axios.post(`${getApiBaseUrl()}/Budget`, data, {
            headers: { "Content-Type": "application/json" },
        });
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(stuffDone());
        }
    } catch (error) {
        dispatch(getError(expenseErrorMessage(error)));
    }
};

export const getAllBudgets = (schoolId, filters = {}) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const params = new URLSearchParams(filters).toString();
        const result = await axios.get(buildApiUrl(`/Budgets/${schoolId}?${params}`));
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getBudgetsSuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(expenseErrorMessage(error)));
    }
};

export const getExpenseSummary = (schoolId, filters = {}) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const params = new URLSearchParams(filters).toString();
        const result = await axios.get(buildApiUrl(`/ExpenseSummary/${schoolId}?${params}`));
        if (result.data.message) {
            dispatch(getFailed(result.data.message));
        } else {
            dispatch(getSummarySuccess(result.data));
        }
    } catch (error) {
        dispatch(getError(expenseErrorMessage(error)));
    }
};

export const getExpensesForExport = (schoolId, filters = {}) => async (dispatch) => {
    dispatch(getRequest());

    try {
        const params = new URLSearchParams(filters).toString();
        const result = await axios.get(buildApiUrl(`/ExpensesExport/${schoolId}?${params}`));
        dispatch(underExpenseControl());
        return result.data;
    } catch (error) {
        dispatch(getError(expenseErrorMessage(error)));
        return null;
    }
};
